import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  DailyVideo,
  DailyAudio,
  useDaily,
  useDailyEvent,
  useLocalSessionId,
  useParticipantProperty,
  useParticipantIds,
  useScreenShare
} from '@daily-co/daily-react';
import type { DailyAudioRef } from '@daily-co/daily-react';
import { DailyCallProvider, useDailyCall } from '../../video/DailyCallProvider';
import { seekerDataService } from '../../services/seekerDataService';
import { type Guide, type HomePageData, type Session } from '../../types';

type GuideSummary = {
  name?: string;
  image?: string | null;
};

const generateInitialsAvatar = (name: string) => {
  const nameParts = name.split(' ').filter(Boolean);
  const initials = nameParts.length >= 2
    ? `${nameParts[0][0]}${nameParts[nameParts.length - 1][0]}`.toUpperCase()
    : nameParts[0]?.[0]?.toUpperCase() || '?';

  const colors = [
    'bg-blue-500', 'bg-green-500', 'bg-purple-500', 'bg-pink-500',
    'bg-indigo-500', 'bg-yellow-500', 'bg-red-500', 'bg-teal-500'
  ];
  const colorIndex = name.length % colors.length;

  return { initials, colorClass: colors[colorIndex] };
};

function parseSessionStart(session?: Session | null) {
  if (!session) return null;
  const raw = `${session.session_date}T${session.session_time}`;
  const parsed = new Date(raw);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function formatDuration(totalSeconds: number) {
  const seconds = Math.max(0, totalSeconds);
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainingSeconds = seconds % 60;
  if (hours > 0) {
    return `${hours.toString().padStart(2, '0')}:${minutes
      .toString()
      .padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
  }
  return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
}

function formatSessionStartedAt(session?: Session | null) {
  if (!session) return '';
  try {
    const start = new Date(`${session.session_date}T${session.session_time}`);
    return start.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  } catch {
    return session.session_time;
  }
}

function humanizeSessionType(session?: Session | null) {
  if (!session) return 'Guided Session';
  switch (session.session_type) {
    case 'preparation':
      return 'Preparation Session';
    case 'integration':
      return 'Integration Session';
    case 'journey':
      return 'Journey Session';
    case 'follow_up':
      return 'Follow-up Session';
    case 'consultation':
    default:
      return 'Consultation Session';
  }
}

function describeSessionFocus(session?: Session | null) {
  if (!session) return 'Integration & Reflection';
  switch (session.session_type) {
    case 'preparation':
      return 'Preparation & Intentions';
    case 'integration':
      return 'Integration & Reflection';
    case 'journey':
      return 'Guided Journey';
    case 'follow_up':
      return 'Continued Support';
    case 'consultation':
    default:
      return 'Consultation & Next Steps';
  }
}

type MeetingViewContentProps = {
  guide: Guide | null;
  session: Session;
};

const MeetingViewContent: React.FC<MeetingViewContentProps> = ({ guide, session }) => {
  const navigate = useNavigate();
  const callObject = useDaily();
  const { join, end, roomUrl, token } = useDailyCall();
  const audioRef = useRef<DailyAudioRef>(null);

  const [microphoneOn, setMicrophoneOn] = useState(() => {
    const pref = localStorage.getItem('gc.microphoneEnabled');
    return pref !== 'false';
  });
  const [cameraOn, setCameraOn] = useState(() => {
    const pref = localStorage.getItem('gc.cameraEnabled');
    return pref !== 'false';
  });
  const [screenShareOn, setScreenShareOn] = useState(false);
  const [screenShareUnsupported] = useState(() => {
    // Screen sharing is not supported on iOS/iPadOS Safari
    const ua = navigator.userAgent;
    const isIOS = /iPad|iPhone|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    return isIOS;
  });
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [hasJoined, setHasJoined] = useState(false);
  const [audioBlocked, setAudioBlocked] = useState(false);
  const controlButtonBase =
    'flex h-11 sm:h-14 items-center justify-center rounded-full border border-[#304031]/25 shadow-[0_24px_48px_-32px_rgba(12,10,24,0.85)] transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#FFF6D2]';

  const handlePlayFailed = useCallback((e: {
    type: 'audio' | 'screenAudio';
    sessionId: string;
    target: HTMLAudioElement;
  }) => {
    console.warn(
      `DailyAudio play() failed for ${e.type} (sessionId=${e.sessionId}). Will require a user gesture.`
    );
    setAudioBlocked(true);
  }, []);

  useEffect(() => {
    console.log('[meeting] state check:', { callObject: !!callObject, roomUrl, token: !!token, hasJoined });
    if (!callObject || !roomUrl || !token || hasJoined) return;

    let cancelled = false;
    (async () => {
      try {
        console.log('[meeting] attempting to join with roomUrl:', roomUrl);
        await join();
        if (!cancelled) {
          console.log('[meeting] successfully joined');
          setHasJoined(true);
        }
      } catch (err) {
        if (!cancelled) {
          console.error('[meeting] join failed', err);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [callObject, roomUrl, token, join, hasJoined]);

  // Cleanup on unmount only
  useEffect(() => {
    return () => {
      console.log('[meeting] component unmounting, ending call');
      void end().catch((err) => console.warn('[meeting] end warning', err));
    };
  }, [end]);

  useEffect(() => {
    if (!callObject || !hasJoined) return;
    console.log('[meeting] Setting local audio to:', microphoneOn);
    try {
      callObject.setLocalAudio(microphoneOn);
    } catch (err) {
      console.warn('[meeting] setLocalAudio failed', err);
    }
  }, [callObject, microphoneOn, hasJoined]);

  useEffect(() => {
    if (!callObject || !hasJoined) return;
    console.log('[meeting] Setting local video to:', cameraOn);
    try {
      callObject.setLocalVideo(cameraOn);
    } catch (err) {
      console.warn('[meeting] setLocalVideo failed', err);
    }
  }, [callObject, cameraOn, hasJoined]);

  useDailyEvent('joined-meeting', (ev) => {
    console.log('[daily] joined-meeting event:', ev);
    setHasJoined(true);
  });
  useDailyEvent('participant-joined', (ev) => {
    console.log('[daily-seeker] participant-joined:', ev?.participant);
    console.log('[daily-seeker] participant details:', {
      sessionId: ev?.participant?.session_id,
      user_name: ev?.participant?.user_name,
      local: ev?.participant?.local,
      video: ev?.participant?.video,
      tracks: ev?.participant?.tracks
    });
  });
  useDailyEvent('participant-updated', (ev) => {
    if (ev?.participant?.local) {
      console.log('[daily] local participant updated:', ev.participant);
      const tracks = ev.participant.tracks;
      if (tracks?.audio) setMicrophoneOn(tracks.audio.state !== 'off');
      if (tracks?.video) setCameraOn(tracks.video.state !== 'off');
    } else {
      // Remote participant updated
      console.log('[daily-seeker] remote participant updated:', {
        sessionId: ev?.participant?.session_id,
        user_name: ev?.participant?.user_name,
        videoState: ev?.participant?.tracks?.video?.state,
        audioState: ev?.participant?.tracks?.audio?.state
      });
    }
  });
  useDailyEvent('track-started', (ev) => {
    if (ev?.participant?.local) {
      console.log('[daily] local track started:', ev.track?.kind);
      if (ev.track?.kind === 'audio') setMicrophoneOn(true);
      if (ev.track?.kind === 'video') setCameraOn(true);
    } else {
      console.log('[daily-seeker] remote track started:', {
        kind: ev.track?.kind,
        participantId: ev?.participant?.session_id,
        userName: ev?.participant?.user_name
      });
    }
  });
  useDailyEvent('track-stopped', (ev) => {
    if (ev?.participant?.local) {
      console.log('[daily] local track stopped:', ev.track?.kind);
      if (ev.track?.kind === 'audio') setMicrophoneOn(false);
      if (ev.track?.kind === 'video') setCameraOn(false);
    } else {
      console.log('[daily-seeker] remote track stopped:', {
        kind: ev.track?.kind,
        participantId: ev?.participant?.session_id
      });
    }
  });
  useDailyEvent('input-settings-updated', () => {
    // Future: sync stored device IDs if needed
  });
  useDailyEvent('camera-error', (ev) => {
    console.error('[daily] camera-error', ev);
  });
  useDailyEvent('error', (ev) => {
    console.error('[daily] error event:', ev);
  });
  useDailyEvent('local-screen-share-started', () => setScreenShareOn(true));
  useDailyEvent('local-screen-share-stopped', () => setScreenShareOn(false));

  // Handle audio autoplay for iOS/Safari
  useDailyEvent('joined-meeting', async () => {
    try {
      await callObject?.startAudio?.();
      setAudioBlocked(false);
    } catch (err) {
      setAudioBlocked(true);
    }
  });

  useDailyEvent('active-speaker-change', async () => {
    if (audioBlocked) {
      try {
        await callObject?.startAudio?.();
        setAudioBlocked(false);
      } catch {}
    }
  });

  const localSessionId = useLocalSessionId();
  const remoteIds = useParticipantIds({ filter: 'remote' });
  const { screens } = useScreenShare();

  // Find remote screen shares (not our own)
  const remoteScreenShares = useMemo(
    () => screens.filter((s) => !s.local && s.screenVideo?.state === 'playable'),
    [screens]
  );

  useEffect(() => {
    console.log('[meeting-seeker] Remote participants:', {
      remoteIds,
      count: remoteIds.length,
      hasJoined,
      localSessionId
    });
  }, [remoteIds, hasJoined, localSessionId]);

  // Subscribe to only the specific properties we need using the actual session ID
  const audioTrackState = useParticipantProperty(localSessionId, 'tracks.audio.state');
  const videoTrackState = useParticipantProperty(localSessionId, 'tracks.video.state');
  const videoTrack = useParticipantProperty(localSessionId, 'tracks.video.track');

  const audioState = audioTrackState;
  const videoState = videoTrackState;
  const isMicOn = audioState ? audioState !== 'off' : false;
  const isCamOn = videoState ? videoState !== 'off' : false;

  // Check for all possible playable states
  const localVideoPlayable = videoState === 'playable' ||
                             videoState === 'sendable' ||
                             videoState === 'loading' ||
                             videoState === 'interrupted';

  const localVideoMessage =
    videoState === 'blocked'
      ? 'Camera blocked'
      : videoState === 'off'
        ? 'Camera off'
        : !videoState
        ? 'Initializing...'
        : `Camera: ${videoState}`;

  useEffect(() => {
    console.log('[meeting] Local participant state:', {
      localSessionId,
      videoState,
      audioState,
      localVideoPlayable,
      isCamOn,
      isMicOn,
      hasVideoTrack: !!videoTrack,
    });
  }, [localSessionId, videoState, audioState, localVideoPlayable, isCamOn, isMicOn, videoTrack]);

  useEffect(() => {
    const start = parseSessionStart(session) ?? new Date();
    const initial = Math.floor((Date.now() - start.getTime()) / 1000);
    setElapsedSeconds(initial > 0 ? initial : 0);
    const timer = window.setInterval(() => {
      setElapsedSeconds((prev) => prev + 1);
    }, 1000);
    return () => window.clearInterval(timer);
  }, [session]);

  const guideSummary: GuideSummary | null = useMemo(() => {
    if (guide) {
      return {
        name: guide.name,
        image: guide.image ?? null
      };
    }

    if (session.guide_name) {
      return { name: session.guide_name };
    }

    return null;
  }, [guide, session]);

  const guideName = guideSummary?.name ?? 'Your Guide';
  const guideAvatar = guideSummary?.image || null;

  const sessionTypeLabel = humanizeSessionType(session);
  const sessionFocusDescription = describeSessionFocus(session);
  const sessionStartedAt = formatSessionStartedAt(session);

  const toggleScreenShare = async () => {
    if (!callObject) return;
    if (screenShareUnsupported) {
      alert('Screen sharing is not supported on this device.');
      return;
    }
    try {
      if (screenShareOn) {
        await callObject.stopScreenShare();
      } else {
        await callObject.startScreenShare();
      }
    } catch (err: any) {
      console.error('[meeting] screenshare toggle failed', err);
      // GCS-114: Show user-facing error instead of silently failing
      const msg = err?.type === 'not-allowed'
        ? 'Screen share permission was denied. Please allow screen sharing in your browser settings.'
        : 'Unable to share screen. Please check your browser permissions and try again.';
      setError(msg);
    }
  };

  const handleLeave = useCallback(async () => {
    try {
      await end();
    } finally {
      navigate('/home');
    }
  }, [end, navigate]);

  return (
    <>
    <div className="flex h-screen w-full flex-col overflow-hidden bg-[#2C2731] px-2 py-2 sm:px-4 sm:py-6 lg:px-8">
      <div className="flex flex-1 flex-col overflow-hidden rounded-2xl border border-[#2C2731]/40 bg-[#FEF6D6] shadow-[0_32px_80px_-42px_rgba(15,8,27,0.85)] sm:rounded-[32px]">
        <header className="flex items-center justify-between border-b border-[#2C2731]/10 bg-[#FEF6D6] px-6 py-4">
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-[#2C2731] text-lg font-semibold text-[#FEF6D6]">
              GC
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.35em] text-[#214D3C]">Guides Collective</p>
              <p className="text-base font-semibold text-[#2C2731]">Session Room</p>
            </div>
          </div>
          <button
            type="button"
            onClick={handleLeave}
            className="rounded-full bg-[#E54848] px-4 py-2 text-sm font-semibold text-white shadow-[0_18px_36px_-20px_rgba(229,72,72,0.8)] transition hover:bg-[#d23d3d] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#E54848]/60 focus-visible:ring-offset-2 focus-visible:ring-offset-[#FEF6D6]"
          >
            Leave
          </button>
        </header>

        <main className="ph-no-capture flex flex-1 flex-col overflow-hidden min-h-0 bg-[#1F1924] text-white">
        <div className="relative flex flex-1 flex-col overflow-hidden min-h-0">
          <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-[#1F1A24]/40 to-[#1F1924] z-0" />

          <div className="absolute left-4 top-4 z-20 flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.2em] text-[#FFF6D2]">
            <span className="flex h-2.5 w-2.5 rounded-full bg-[#44FFA1]" />
            Connected
          </div>

          <div className="absolute left-4 top-14 z-20 rounded-full bg-white/10 px-3 py-1 text-sm font-semibold text-[#FFF6D2]">
            {formatDuration(elapsedSeconds)}
          </div>

          <div className="pointer-events-none absolute right-3 top-3 z-30 w-24 overflow-hidden rounded-xl border-2 border-white/80 bg-black shadow-xl aspect-video sm:right-4 sm:top-4 sm:w-40">
            {(localVideoPlayable || videoTrack) && localSessionId ? (
              <DailyVideo
                type="video"
                sessionId={localSessionId}
                mirror
                muted
                playsInline
                autoPlay
                controls={false}
                className="h-full w-full object-cover"
              />
            ) : (
              <div className="flex h-full w-full flex-col items-center justify-center gap-1 bg-gray-900 px-2 text-center text-[10px] font-medium uppercase tracking-wide text-white">
                <span>{localVideoMessage}</span>
              </div>
            )}
          </div>

          <div className="relative z-10 flex h-full w-full items-center justify-center px-4 pb-6 pt-16">
            {remoteIds.length > 0 ? (
              <div className="relative w-full h-full">
                {/* Remote screen shares take priority and fill the main area */}
                {remoteScreenShares.length > 0 && (
                  <div className="absolute inset-0 z-10 overflow-hidden rounded-xl border-2 border-white/70 bg-black">
                    <DailyVideo
                      type="screenVideo"
                      sessionId={remoteScreenShares[0].session_id}
                      playsInline
                      autoPlay
                      controls={false}
                      className="h-full w-full object-contain"
                    />
                    <div className="absolute left-3 top-3 rounded-full bg-black/60 px-3 py-1 text-xs font-medium text-white">
                      Screen Share
                    </div>
                  </div>
                )}
                <div className={remoteScreenShares.length > 0
                  ? 'absolute bottom-3 right-3 z-20 grid w-32 grid-cols-1 gap-2 sm:w-48'
                  : 'grid w-full grid-cols-1 gap-3'
                }>
                {remoteIds.map((id) => (
                  <div
                    key={id}
                    className="relative aspect-video overflow-hidden rounded-xl border-2 border-white/70 bg-black"
                  >
                    <DailyVideo
                      type="video"
                      sessionId={id}
                      playsInline
                      autoPlay
                      muted
                      controls={false}
                      className="h-full w-full object-cover"
                    />
                  </div>
                ))}
                </div>
              </div>
            ) : (
              <div className="flex max-w-sm flex-col items-center text-center text-[#FEF6D6]">
                <div className="mb-6 flex items-center justify-center">
                  {guideAvatar ? (
                    <div className="flex h-32 w-32 items-center justify-center overflow-hidden rounded-full border-4 border-white/80 bg-gray-700 shadow-lg">
                      <img src={guideAvatar} alt={guideName} className="h-full w-full object-cover" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; (e.target as HTMLImageElement).parentElement!.innerHTML = `<span class="text-5xl font-semibold text-white">${generateInitialsAvatar(guideName).initials}</span>`; }} />
                    </div>
                  ) : (
                    (() => {
                      const { initials, colorClass } = generateInitialsAvatar(guideName);
                      return (
                        <div className={`flex h-32 w-32 items-center justify-center rounded-full border-4 border-white/80 ${colorClass} shadow-lg text-white text-5xl font-semibold`}>
                          {initials}
                        </div>
                      );
                    })()
                  )}
                </div>
                <h1 className="text-xl font-semibold text-white ph-mask">{guideName}</h1>
                <p className="mt-1 text-sm text-[#FFF6D2]/80">Your guide will join shortly</p>
              </div>
            )}
          </div>
        </div>

        <div className="shrink-0 border-t border-white/10 bg-[#1F1924]/40">
          <div className="mx-auto flex max-w-2xl justify-center px-3 pb-3 pt-3 sm:px-4 sm:pb-8 sm:pt-6">
            <div className={`grid w-full ${screenShareUnsupported ? 'grid-cols-3' : 'grid-cols-4'} gap-2 sm:gap-4`}>
              <button
                type="button"
                onClick={() => setMicrophoneOn((prev) => !prev)}
                className={`${controlButtonBase} ${
                  microphoneOn ? 'bg-[#FEF6D6] text-[#2C2731]' : 'bg-[#2C2731] text-[#FEF6D6]'
                }`}
                aria-pressed={microphoneOn}
                aria-label={microphoneOn ? 'Mute microphone' : 'Unmute microphone'}
              >
                <svg
                  className="h-5 w-5"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M14 8.5C14 10.9853 11.9853 13 9.5 13C7.01472 13 5 10.9853 5 8.5V5.5C5 3.01472 7.01472 1 9.5 1C11.9853 1 14 3.01472 14 5.5V8.5Z"
                    stroke="currentColor"
                    strokeWidth="1.6"
                  />
                  <path
                    d="M2.5 9.5C2.5 12.8137 5.18629 15.5 8.5 15.5H10.5C13.8137 15.5 16.5 12.8137 16.5 9.5"
                    stroke="currentColor"
                    strokeWidth="1.6"
                    strokeLinecap="round"
                  />
                </svg>
              </button>

              <button
                type="button"
                onClick={() => setCameraOn((prev) => !prev)}
                className={`${controlButtonBase} ${
                  cameraOn ? 'bg-[#FEF6D6] text-[#2C2731]' : 'bg-[#2C2731] text-[#FEF6D6]'
                }`}
                aria-pressed={cameraOn}
                aria-label={cameraOn ? 'Turn camera off' : 'Turn camera on'}
              >
                <svg
                  className="h-5 w-5"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M4 5C4 3.34315 5.34315 2 7 2H11C12.6569 2 14 3.34315 14 5V11C14 12.6569 12.6569 14 11 14H7C5.34315 14 4 12.6569 4 11V5Z"
                    stroke="currentColor"
                    strokeWidth="1.6"
                  />
                  <path
                    d="M16 7L18 5V15L16 13"
                    stroke="currentColor"
                    strokeWidth="1.6"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>

              {!screenShareUnsupported && (
              <button
                type="button"
                onClick={toggleScreenShare}
                className={`${controlButtonBase} ${
                  screenShareOn ? 'bg-[#2C2731] text-[#FEF6D6]' : 'bg-[#FEF6D6] text-[#2C2731]'
                }`}
                aria-pressed={screenShareOn}
                aria-label={screenShareOn ? 'Stop sharing screen' : 'Share screen'}
              >
                <svg
                  className="h-5 w-5"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M3 4H17C17.5523 4 18 4.44772 18 5V11C18 11.5523 17.5523 12 17 12H3C2.44772 12 2 11.5523 2 11V5C2 4.44772 2.44772 4 3 4Z"
                    stroke="currentColor"
                    strokeWidth="1.6"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M6 16H14"
                    stroke="currentColor"
                    strokeWidth="1.6"
                    strokeLinecap="round"
                  />
                  <path
                    d="M10 9L12 7M10 9L8 7M10 9V6"
                    stroke="currentColor"
                    strokeWidth="1.6"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <span className="sr-only">
                  {screenShareOn ? 'Stop sharing screen' : 'Share screen'}
                </span>
              </button>
              )}

              <button
                type="button"
                onClick={handleLeave}
                className="flex h-11 items-center justify-center rounded-full border border-transparent bg-gradient-to-r from-[#E54848] to-[#F66F6F] text-white shadow-[0_24px_48px_-28px_rgba(229,72,72,0.9)] transition hover:brightness-110 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#E54848] sm:h-14"
                aria-label="Leave session"
              >
                <svg
                  className="h-5 w-5"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M4 10C4 7.23858 6.23858 5 9 5H11C13.7614 5 16 7.23858 16 10M7 15H9M11 15H13"
                    stroke="currentColor"
                    strokeWidth="1.6"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M1.5 10H4M16 10H18.5"
                    stroke="currentColor"
                    strokeWidth="1.6"
                    strokeLinecap="round"
                  />
                </svg>
              </button>
            </div>
          </div>
        </div>

        <section className="shrink-0 border-t border-[#2C2731]/10 bg-[#FEF6D6]">
          <div className="mx-auto max-w-md px-4 py-3 text-center text-[#214D3C] sm:px-6 sm:py-6">
            <h2 className="text-base font-semibold text-[#2C2731]">{sessionTypeLabel}</h2>
            <p className="mt-1 text-sm text-[#214D3C]/80">{sessionFocusDescription}</p>
            <div className="mt-2 hidden items-center justify-center gap-6 text-xs text-[#214D3C]/70 sm:mt-4 sm:flex">
              <div className="flex items-center gap-2">
                <svg
                  className="h-3 w-3"
                  viewBox="0 0 12 12"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M6 1V3M6 9V11M3 6H1M11 6H9M9.5 6C9.5 8.48528 7.48528 10.5 5 10.5C2.51472 10.5 0.5 8.48528 0.5 6C0.5 3.51472 2.51472 1.5 5 1.5C7.48528 1.5 9.5 3.51472 9.5 6Z"
                    stroke="currentColor"
                    strokeWidth="1.2"
                    strokeLinecap="round"
                  />
                </svg>
                <span>Started {sessionStartedAt}</span>
              </div>
              <div className="flex items-center gap-2">
                <svg
                  className="h-3 w-3"
                  viewBox="0 0 12 12"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M6 1L10.3301 3.5V8.5L6 11L1.66987 8.5V3.5L6 1Z"
                    stroke="currentColor"
                    strokeWidth="1.2"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M3.5 6L5.5 8L8.5 4"
                    stroke="currentColor"
                    strokeWidth="1.2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <span>Encrypted</span>
              </div>
            </div>
          </div>
        </section>
      </main>
      </div>
      </div>

      <DailyAudio
        ref={audioRef}
        maxSpeakers={6}
        onPlayFailed={handlePlayFailed}
      />

      {audioBlocked && (
        <div className="fixed bottom-4 left-0 right-0 z-50 mx-auto flex max-w-sm items-center justify-between rounded-2xl border border-[#2C2731]/15 bg-[#FEF6D6] px-4 py-3 text-[#2C2731] shadow-[0_20px_40px_-28px_rgba(12,10,24,0.7)]">
          <span className="text-sm font-medium">Audio is blocked by the browser.</span>
          <button
            type="button"
            onClick={async () => {
              try {
                const els = audioRef.current?.getAllAudio() ?? [];
                await Promise.allSettled(els.map(a => a.play()));
                await callObject?.startAudio?.();
                setAudioBlocked(false);
              } catch (err) {
                console.error('Manual resume failed', err);
              }
            }}
            className="rounded-full bg-[#2C2731] px-3 py-1 text-sm font-semibold text-[#FEF6D6] hover:bg-[#1F1924]"
          >
            Enable audio
          </button>
        </div>
      )}
    </>
  );
};

export const MeetingView: React.FC = () => {
  const navigate = useNavigate();
  const [guide, setGuide] = useState<Guide | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;

    const load = async () => {
      try {
        setLoading(true);
        const data: HomePageData = await seekerDataService.getHomePageData();
        if (!mounted) return;
        setGuide(data.match?.guide_details ?? null);
        setSession(data.upcomingSession ?? null);
        setError(null);
      } catch (err) {
        if (!mounted) return;
        const message =
          err instanceof Error ? err.message : 'We could not load your meeting details.';
        setError(message);
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    load();

    return () => {
      mounted = false;
    };
  }, []);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#2C2731] px-4">
        <div className="text-center text-[#FEF6D6]">
          <div className="mx-auto mb-4 h-12 w-12 animate-spin rounded-full border-b-2 border-[#FEF6D6]" />
          <p className="text-[#FEF6D6]/80">Connecting to your session…</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#2C2731] px-4">
        <div className="max-w-sm rounded-3xl border border-[#FEF6D6]/20 bg-[#FEF6D6] p-6 text-center shadow-[0_32px_60px_-40px_rgba(15,8,27,0.9)]">
          <p className="mb-4 text-sm font-medium text-[#B42318]">{error}</p>
          <button
            type="button"
            onClick={() => navigate('/home/waiting-room')}
            className="rounded-full bg-[#2C2731] px-4 py-2 text-sm font-semibold text-[#FEF6D6] hover:bg-[#1F1924] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2C2731]/60 focus-visible:ring-offset-2 focus-visible:ring-offset-[#FEF6D6]"
          >
            Return to Waiting Room
          </button>
        </div>
      </div>
    );
  }

  if (!session) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#2C2731] px-4">
        <div className="max-w-sm rounded-3xl border border-[#FEF6D6]/20 bg-[#FEF6D6] p-6 text-center shadow-[0_32px_60px_-40px_rgba(15,8,27,0.9)]">
          <p className="mb-4 text-sm text-[#2C2731]">
            We couldn't find an in-progress session. If you believe this is a mistake, head
            back to the waiting room and try again.
          </p>
          <button
            type="button"
            onClick={() => navigate('/home/waiting-room')}
            className="rounded-full bg-[#2C2731] px-4 py-2 text-sm font-semibold text-[#FEF6D6] hover:bg-[#1F1924] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2C2731]/60 focus-visible:ring-offset-2 focus-visible:ring-offset-[#FEF6D6]"
          >
            Return to Waiting Room
          </button>
        </div>
      </div>
    );
  }

  return (
    <DailyCallProvider key={session.id} sessionId={session.id}>
      <MeetingViewContent guide={guide} session={session} />
    </DailyCallProvider>
  );
};

export default MeetingView;
